"use client";

import { Popover as PopoverPrimitive } from "@base-ui/react/popover";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@ui/components/tooltip";
import { cn } from "@ui/lib";
import { InfoIcon } from "lucide-react";
import { type ReactNode, useRef, useState } from "react";

/**
 * A small ⓘ next to a label that explains it on demand. Opens on hover, focus
 * and tap (a popover, not a tooltip, so it also works on phones). Use it for
 * anything a first-time user might wonder about but a regular user doesn't
 * need to read every time.
 *
 * `<FieldLabel>Temperature <InfoTip>Lower gives more consistent replies.</InfoTip></FieldLabel>`
 */
export function InfoTip({
	children,
	label = "More info",
	side = "top",
	className,
}: {
	children: ReactNode;
	/** Accessible name for the icon button. */
	label?: string;
	side?: "top" | "bottom" | "left" | "right";
	className?: string;
}) {
	const [open, setOpen] = useState(false);
	// How it was opened: a click after a hover-open pins it instead of closing
	// it, while a second tap on a phone still closes it.
	const openedBy = useRef<string | null>(null);

	return (
		<PopoverPrimitive.Root
			open={open}
			onOpenChange={(next, { reason }) => {
				if (
					!next &&
					reason === "trigger-press" &&
					openedBy.current === "trigger-hover"
				) {
					openedBy.current = reason;
					return;
				}
				// Pinned by a click: moving the pointer away doesn't close it.
				if (
					!next &&
					reason === "trigger-hover" &&
					openedBy.current === "trigger-press"
				) {
					return;
				}
				openedBy.current = next ? reason : null;
				setOpen(next);
			}}
		>
			<PopoverPrimitive.Trigger
				openOnHover
				delay={120}
				closeDelay={80}
				aria-label={label}
				data-slot="info-tip"
				className={cn(
					"relative inline-grid size-4 shrink-0 translate-y-px place-items-center rounded-full align-middle text-muted-foreground/70 outline-none transition-colors after:absolute after:-inset-2 hover:text-foreground focus-visible:text-foreground focus-visible:ring-3 focus-visible:ring-ring/30 data-popup-open:text-foreground",
					className,
				)}
				onClick={(event) => {
					// Inside a <label> a click would also focus or toggle the control.
					event.preventDefault();
					event.stopPropagation();
				}}
			>
				<InfoIcon className="size-3.5" aria-hidden />
			</PopoverPrimitive.Trigger>
			<PopoverPrimitive.Portal>
				<PopoverPrimitive.Positioner
					side={side}
					sideOffset={6}
					collisionPadding={12}
					className="isolate z-50"
				>
					<PopoverPrimitive.Popup
						data-slot="info-tip-content"
						className="z-50 max-w-72 origin-(--transform-origin) text-pretty rounded-2xl bg-popover px-3 py-2 font-normal text-popover-foreground text-sm shadow-(--shadow-overlay) ring-1 ring-foreground/8 outline-none animation-duration-100 data-closed:animate-out data-open:animate-in data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95"
					>
						{children}
					</PopoverPrimitive.Popup>
				</PopoverPrimitive.Positioner>
			</PopoverPrimitive.Portal>
		</PopoverPrimitive.Root>
	);
}

/**
 * Explains why a control is disabled. Disabled buttons don't receive pointer
 * events, so the tooltip lives on a wrapper that stays mounted either way.
 *
 * `<DisabledReason reason={!dirty && "Nothing to save yet"}><Button disabled={!dirty}>Save</Button></DisabledReason>`
 */
export function DisabledReason({
	reason,
	children,
	side = "top",
}: {
	reason: ReactNode | false | null | undefined;
	children: ReactNode;
	side?: "top" | "bottom" | "left" | "right";
}) {
	// The wrapper always renders so focus isn't lost when the reason comes and
	// goes; only the tooltip switches on and off.
	return (
		<Tooltip disabled={!reason}>
			<TooltipTrigger
				render={<span className="inline-flex" />}
				// Focusable only while there's a reason, so keyboard users hear it.
				tabIndex={reason ? 0 : undefined}
			>
				{children}
			</TooltipTrigger>
			<TooltipContent side={side}>{reason}</TooltipContent>
		</Tooltip>
	);
}
